import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';
import { ExpenseLedgerEntry } from './expenseLedgerService';

interface UserDetailsModalProps {
  open: boolean;
  entry: ExpenseLedgerEntry | any | null;
  onClose: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ open, entry, onClose }) => {
  if (!entry || !open) return null;

  const userDetails = (entry.userDetails || {}) as {
    name?: string;
    email?: string;
    phone?: string;
    dob?: string;
    country_res?: string;
    state?: string;
    surburb?: string;
    postcode?: string;
    role?: string[];
    groups?: string[];
    department?: string;
  };

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (typeof numAmount !== 'number' || isNaN(numAmount)) return '$0.00';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      incomeUnits: 'Income Units',
      growthUnits: 'Growth Units',
      incomeContribution: 'Income Contribution',
      growthContribution: 'Growth Contribution',
      incomeUnitSell: 'Income Unit Sell',
      growthUnitSell: 'Growth Unit Sell',
      incomeAssetPurchase: 'Income Asset Purchase',
      growthAssetPurchase: 'Growth Asset Purchase',
      incomeAssetRevaluation: 'Income Asset Revaluation',
      growthAssetRevaluation: 'Growth Asset Revaluation',
      incomeAssetSell: 'Income Asset Sell',
      growthAssetSell: 'Growth Asset Sell',
      incomeRevaluation: 'Income Revaluation',
      growthRevaluation: 'Growth Revaluation',
      cashUsed: 'Operational Expense'
    };
    return labels[type] || type;
  };

  // Get transaction-specific fields based on type
  const getTransactionSpecificFields = () => {
    const type = entry.type || entry.expenseType || '';
    const fields: { label: string; value: any; format?: 'currency' | 'text' }[] = [];

    if (entry.isOperationChargeEntry) {
      fields.push({ label: 'Charge Entry Of', value: entry.chargeEntryOf || 'N/A', format: 'text' });
      fields.push({ label: 'Investor Email', value: entry.relatedInvestorEmail || 'N/A', format: 'text' });
      fields.push({ label: 'Investor Name', value: entry.investorName || 'N/A', format: 'text' });
      fields.push({ label: 'Request ID', value: entry.relatedRequestId || 'N/A', format: 'text' });
      return fields;
    }

    // Asset Purchase Fields
    if (type.includes('AssetPurchase')) {
      fields.push({ label: 'Asset Identifier', value: entry.assetIdentifier || 'N/A', format: 'text' });
      fields.push({ label: 'Asset ID', value: entry.relatedAssetId || 'N/A', format: 'text' });
      fields.push({ label: 'Purchase Value', value: entry.purchaseValue || entry.amount || 'N/A', format: 'currency' });
      fields.push({ label: 'Cash Used', value: entry.cashUsed || 'N/A', format: 'currency' });
      fields.push({ label: 'Operation Charge', value: entry.operationCharge || 'N/A', format: 'currency' });
      fields.push({ label: 'Request ID', value: entry.relatedRequestId || 'N/A', format: 'text' });
    }

    // Asset Revaluation Fields
    if (type.includes('Revaluation')) {
      fields.push({ label: 'Asset Identifier', value: entry.assetIdentifier || 'N/A', format: 'text' });
      fields.push({ label: 'Asset ID', value: entry.relatedAssetId || 'N/A', format: 'text' });
      fields.push({ label: 'Old Value', value: entry.oldValue || 'N/A', format: 'currency' });
      fields.push({ label: 'New Value', value: entry.newValue || 'N/A', format: 'currency' });
      fields.push({ label: 'Value Change', value: entry.valueChange || entry.amount || 'N/A', format: 'currency' });
    }

    // Asset Sale Fields
    if (type.includes('AssetSell')) {
      fields.push({ label: 'Asset Identifier', value: entry.assetIdentifier || 'N/A', format: 'text' });
      fields.push({ label: 'Asset ID', value: entry.relatedAssetId || 'N/A', format: 'text' });
      fields.push({ label: 'Sale Value', value: entry.saleValue || entry.amount || 'N/A', format: 'currency' });
      fields.push({ label: 'Operation Charge', value: entry.operationCharge || 'N/A', format: 'currency' });
      fields.push({ label: 'Deductions', value: entry.deductions || 'N/A', format: 'currency' });
      fields.push({ label: 'Request ID', value: entry.relatedRequestId || 'N/A', format: 'text' });
    }

    // Unit Contribution/Sell Fields
    if (type.includes('Contribution') || type.includes('UnitSell')) {
      fields.push({ label: 'Investor Email', value: entry.relatedInvestorEmail || 'N/A', format: 'text' });
      fields.push({ label: 'Investor Name', value: entry.investorName || 'N/A', format: 'text' });
      fields.push({ label: 'Units Amount', value: entry.unitsAmount || 'N/A', format: 'text' });
      fields.push({ label: 'Unit Price', value: entry.unitPrice || 'N/A', format: 'currency' });
      fields.push({ label: 'Request ID', value: entry.relatedRequestId || 'N/A', format: 'text' });
    }

    // Operational Expense Fields
    if (type === 'cashUsed') {
      fields.push({ label: 'Asset/Fund ID', value: entry.relatedAssetId || 'N/A', format: 'text' });
      fields.push({ label: 'Asset Identifier', value: entry.assetIdentifier || 'N/A', format: 'text' });
      fields.push({ label: 'Management Fee', value: entry.managementFee || 'N/A', format: 'currency' });
      fields.push({ label: 'Operation Charge', value: entry.operationCharge || 'N/A', format: 'currency' });
    }

    // Return fields only if type matches
    return fields.length > 0 ? fields : [];
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Transaction Details & User Information
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {/* Transaction Details Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
            📋 Transaction Information
          </Typography>
          <Table size="small" sx={{ '& .MuiTableCell-root': { border: 'none', py: 1 } }}>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '40%' }}>Transaction ID:</TableCell>
                <TableCell sx={{ color: '#666' }}>{entry.expenseId}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Type:</TableCell>
                <TableCell>
                  <Chip
                    label={getTransactionTypeLabel(entry.type || entry.expenseType || 'Unknown')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Amount:</TableCell>
                <TableCell sx={{ fontWeight: 500, color: '#2e7d32' }}>
                  {formatCurrency(entry.amount)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date:</TableCell>
                <TableCell sx={{ color: '#666' }}>
                  {formatDate(entry.date)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Description:</TableCell>
                <TableCell sx={{ color: '#666' }}>{entry.description}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* User Details Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
            👤 User Information
          </Typography>
          <Table size="small" sx={{ '& .MuiTableCell-root': { border: 'none', py: 1 } }}>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '40%' }}>Name:</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{userDetails?.name || '-'}</TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Email:</TableCell>
                <TableCell sx={{ color: '#1976d2' }}>
                  <a href={`mailto:${userDetails?.email}`} style={{ textDecoration: 'none' }}>
                    {userDetails?.email || '-'}
                  </a>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Phone:</TableCell>
                <TableCell sx={{ color: '#666' }}>{userDetails?.phone || '-'}</TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Date of Birth:</TableCell>
                <TableCell sx={{ color: '#666' }}>{userDetails?.dob || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Address Details Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
            📍 Address Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                  Country
                </Typography>
                <Typography variant="body2">{userDetails?.country_res || '-'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                  State
                </Typography>
                <Typography variant="body2">{userDetails?.state || '-'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                  Suburb
                </Typography>
                <Typography variant="body2">{userDetails?.surburb || '-'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                  Postcode
                </Typography>
                <Typography variant="body2">{userDetails?.postcode || '-'}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Transaction-Specific Details Section */}
        {getTransactionSpecificFields().length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
              💼 Transaction Details
            </Typography>
            <Table size="small" sx={{ '& .MuiTableCell-root': { border: 'none', py: 1 } }}>
              <TableBody>
                {getTransactionSpecificFields().map((field, idx) => (
                  <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#f5f5f5' : 'transparent' }}>
                    <TableCell sx={{ fontWeight: 600, width: '40%' }}>{field.label}:</TableCell>
                    <TableCell sx={{ color: '#666' }}>
                      {field.format === 'currency'
                        ? formatCurrency(field.value)
                        : field.value || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsModal;
